import type { Result } from '@avalara/types/index';
import promisePoller from 'promise-poller';

import type { RouteComponentProps } from 'react-router-dom';
import conf from '../../conf';
import { setObligationProvisioningStatus, setProvisioningDelayed } from '../../store/offer';
import analytics from '../analytics';
import fetch from '../fetch';
import { embeddedViewRequested } from '../helpers';
import { resetOwdBearerTokenCache } from '../owd-bearer-token';
import { getSystemErrorMessaging } from './error';
import { goToNextOfferStep } from './offer-flow';

import type { Dispatch } from '@avalara/types/actions';
import type { State } from '@avalara/types/reducer-states';
import type { OfferPlan, SoldOffers } from '../../../../types/offers';

type CheckFunction<T> = (result: Result<T>) => boolean;
type ProvisioningCheckResult = 'success' | 'failed' | null;

// True when the response indicates the request's bearer token is unauthenticated -- either
// rejected outright (401) or accepted by transport but flagged by the BFF's own auth middleware
// (see src/server/middleware/route-specific.ts's isLoggedIn check).
const isUnauthenticatedResult = (result: Result<unknown>): boolean =>
  (!result.ok && result.status === 401) ||
  (result.ok && (result.value as { unauthenticatedUser?: boolean } | null)?.unauthenticatedUser === true);

export const checkForProvisioning = async <T = unknown>(
  dispatch: Dispatch,
  check: CheckFunction<T>,
  url: string,
  signal?: AbortSignal,
): Promise<ProvisioningCheckResult> => {
  const doFetch = () => fetch(dispatch, { showLoadingWheel: false })(url, { signal });

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  let result: Result<any> = await doFetch();

  // The memoized OWD bearer token can outlive its own expiry over a long poll -- see
  // owd-bearer-token.ts. Evict it and retry once with a freshly-fetched token rather than
  // treating an expired-token response as a terminal failure. Scoped to polling (not fetch.ts)
  // so other, mutating call sites keep their existing fail/redirect behavior instead of silently
  // auto-retrying a stale request.
  if (isUnauthenticatedResult(result)) {
    resetOwdBearerTokenCache();
    result = await doFetch();
  }

  if (!result.ok || result.value.unauthenticatedUser) return 'failed';
  return check(result) ? 'success' : null;
};

const FIVE_SECONDS = 5000;
const TWO_MINUTES = 120000;
const THREE_MINUTES = 180000;
const MAX_CONSECUTIVE_FAILURES = 3;

// Wraps a poll check so a run of transient errors (e.g. an expired bearer token before the client
// refreshes it) doesn't end the poll on the first one. Only a 'failed' result MAX_CONSECUTIVE_FAILURES
// in a row propagates as 'failed'; before that it's reported as null (still pending) so
// promisePoller's shouldContinue keeps polling. A 'success' or null result resets the count.
const withTransientFailureTolerance = (
  check: () => Promise<ProvisioningCheckResult>,
): (() => Promise<ProvisioningCheckResult>) => {
  let consecutiveFailures = 0;
  return async () => {
    const result = await check();
    if (result !== 'failed') {
      consecutiveFailures = 0;
      return result;
    }
    consecutiveFailures += 1;
    return consecutiveFailures >= MAX_CONSECUTIVE_FAILURES ? 'failed' : null;
  };
};
const pollProvisioningCheck =
  <T = unknown>(url: string, check: CheckFunction<T>, history: RouteComponentProps['history']) =>
  async (dispatch: Dispatch, getState: () => State) => {
    const {
      account: { accountId },
      offer: { offerFlow },
    } = getState();

    if (!offerFlow) {
      dispatch(getSystemErrorMessaging({ errorCode: 'genericError' }));
      return;
    }

    const checkResultPoll = promisePoller({
      interval: FIVE_SECONDS,
      // continue until we get an error or result
      shouldContinue: (reason, value) => !reason && !value,
      taskFn: withTransientFailureTolerance(() => checkForProvisioning<T>(dispatch, check, url)),
    });

    let hasProvisioned = await Promise.race([
      checkResultPoll,
      new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), THREE_MINUTES);
      }),
    ]);

    // if obligation has not provisioned within 3 minutes, update UI to indicate that it's taking
    // longer than expected
    if (!hasProvisioned) {
      analytics.track('PROVISIONING_DELAYED', {
        accountId,
        offerId: offerFlow,
      });

      dispatch(setProvisioningDelayed(true));
    }

    hasProvisioned = await Promise.race([
      checkResultPoll,
      new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), TWO_MINUTES);
      }),
    ]);

    // if obligation or delivery system still has not provisioned after another 2 minutes
    // (5 minutes total), or if the obligations fetch returned an error, log that
    // the offer check has failed
    if (hasProvisioned !== 'success') {
      analytics.track('OFFER_CHECK_FAILURE', {
        accountId,
        offerId: offerFlow,
      });

      if (hasProvisioned === 'failed') {
        dispatch(getSystemErrorMessaging({ errorCode: 'genericError' }));
      }

      if (hasProvisioned == null) {
        dispatch(getSystemErrorMessaging({ errorCode: 'ips-api-error' }));
        return;
      }
    }

    if (embeddedViewRequested()) {
      window.parent.postMessage(
        {
          event: 'add-offer',
          success: hasProvisioned === 'success',
          type: offerFlow,
        },
        '*',
      );
    }

    // continue polling indefinitely until it returns a success or failure
    hasProvisioned = await checkResultPoll;

    if (hasProvisioned === 'success') dispatch(goToNextOfferStep({ history }));
  };

export const pollForDeliverySystem = (
  system: string,
  history: RouteComponentProps['history'],
  provisioningStatusId?: string,
) =>
  pollProvisioningCheck<{ provisioned: boolean }>(
    `${conf.API_URL}/api/provisioning-status/${provisioningStatusId}/delivery-systems/${system}`,
    (result) => result.ok && result.value.provisioned,
    history,
  );

export const pollForOfferPlan = (
  offerFlow: string,
  selectedPlanName: string,
  history: RouteComponentProps['history'],
) =>
  pollProvisioningCheck<Record<string, OfferPlan>>(
    `${conf.API_URL}/api/accounts/offers/${offerFlow}/status`,
    (result) => result.ok && !!result.value?.[selectedPlanName],
    history,
  );

export const pollForObligationWithConnectorId = (connectorId: string, history: RouteComponentProps['history']) =>
  pollProvisioningCheck<boolean>(
    `${conf.API_URL}/api/accounts/connector/${connectorId}/status`,
    (result) => result.ok && result.value,
    history,
  );

export const pollForSoldOffer = (accountId: string | number, history: RouteComponentProps['history']) =>
  pollProvisioningCheck<SoldOffers[]>(
    `${conf.API_URL}/api/ips/${accountId}/soldOffers`,
    (result) => result.ok,
    history,
  );

const SIXTY_MINUTES = 3600000;

// Unlike pollProvisioningCheck, this never navigates away -- it just reports
// 'pending'/'success'/'failed' to the store so the caller (e.g. the order-confirmation page) can
// render an in-place status card while the user stays put. The option below gives up after an hour
// rather than polling truly forever. `signal` is an exit signal the caller owns (e.g. triggered from
// a useEffect cleanup on unmount/navigation): it's checked before scheduling each new attempt
// (stopping the loop early, rather than waiting out a rejection) and forwarded into
// checkForProvisioning so an in-flight fetch exits too. Any rejection -- the caller exiting, the
// timeout above, or a genuine failure from checkForProvisioning/getOwdBearerToken/the fetch itself --
// is caught and treated as 'failed', except when caused by the caller exiting, in which case nothing
// is dispatched at all: the caller is gone, so there's no store update left to make.
export const pollForObligationProvisioning = (url: string, signal?: AbortSignal) => async (dispatch: Dispatch) => {
  dispatch(setObligationProvisioningStatus('pending'));

  let hasProvisioned: ProvisioningCheckResult;
  try {
    hasProvisioned = await promisePoller({
      interval: FIVE_SECONDS,
      masterTimeout: SIXTY_MINUTES,
      shouldContinue: (reason, value) => !reason && !value,
      taskFn: withTransientFailureTolerance(() => {
        if (signal?.aborted) return Promise.reject(new Error('Polling exited'));
        return checkForProvisioning<boolean>(dispatch, (result) => result.ok && result.value, url, signal);
      }),
    });
  } catch {
    hasProvisioned = 'failed';
  }

  if (signal?.aborted) return;

  dispatch(setObligationProvisioningStatus(hasProvisioned === 'success' ? 'success' : 'failed'));
};
